import { CountryConfig } from '../IncidentMap';
import { AUSTRALIA_STATE_FILES } from './AU';
import { CANADA_PROVINCE_FILES } from './CA';
import { ESTONIA_REGION_FILES } from './EE';
import { SPAIN_PROVINCE_FILES } from './ES';
import { POLAND_VOIVODESHIP_FILES } from './PL';
import { USA_STATE_FILES } from './US';
import { normalizeCaRegion } from './normalizeCARegion';

// This file is the single source of truth for country-specific configuration like geojson URLs and region name properties. The main IncidentMap component imports
// this and doesn't have any hardcoded country-specific logic itself, so adding a
// new country should be as simple as adding a new entry here.

const countries: Record<string, CountryConfig> = {
  EE: {
    geoJsonUrl: '/geojson/estonia-regions.geojson',
    regionNameProperty: 'name',
    postcodeProperty: 'postcode',
    postcodeGeoJsonUrl: (regionName) => {
      const file = ESTONIA_REGION_FILES[regionName];
      return file ? `/geojson/estonia/${file}.geojson` : null;
    },
  },
  ES: {
    geoJsonUrl: '/geojson/spain-regions.geojson',
    regionNameProperty: 'name',
    postcodeProperty: 'COD_POSTAL',
    postcodeGeoJsonUrl: (_, regionFeature) => {
      const code = regionFeature?.properties?.cod_prov;
      const filename = code ? SPAIN_PROVINCE_FILES[code] : null;

      return filename ? `/geojson/spain/${filename}.geojson` : null;
    },
  },
  PL: {
    geoJsonUrl: '/geojson/poland-regions.geojson',
    regionNameProperty: 'name',
    postcodeProperty: 'Name',
    postcodeGeoJsonUrl: (regionName) => {
      const file = POLAND_VOIVODESHIP_FILES[regionName];
      return file ? `/geojson/poland/${file}.geojson` : null;
    },
  },
  LV: { geoJsonUrl: '/geojson/latvia-regions.geojson' },
  LT: { geoJsonUrl: '/geojson/lithuania-regions.geojson' },
  PT: { geoJsonUrl: '/geojson/portugal-regions.geojson' },
  DE: { geoJsonUrl: '/geojson/germany-regions.geojson' },
  DK: { geoJsonUrl: '/geojson/denmark-regions.geojson' },
  FR: { geoJsonUrl: '/geojson/france-regions.geojson' },
  IT: { geoJsonUrl: '/geojson/italy-regions.geojson' },
  GR: { geoJsonUrl: '/geojson/greece-regions.geojson' },
  HU: { geoJsonUrl: '/geojson/hungary-regions.geojson' },
  SE: { geoJsonUrl: '/geojson/sweden-regions.geojson' },
  IE: { geoJsonUrl: '/geojson/ireland-regions.geojson' },
  SK: { geoJsonUrl: '/geojson/slovakia-regions.geojson' },
  SI: { geoJsonUrl: '/geojson/slovenia-regions.geojson' },
  RO: { geoJsonUrl: '/geojson/romania-regions.geojson' },
  MT: { geoJsonUrl: '/geojson/malta-regions.geojson' },
  LU: { geoJsonUrl: '/geojson/luxembourg-regions.geojson' },
  CY: { geoJsonUrl: '/geojson/cyprus-regions.geojson' },
  AT: { geoJsonUrl: '/geojson/austria-regions.geojson' },
  BE: { geoJsonUrl: '/geojson/belgium-regions.geojson' },
  BG: { geoJsonUrl: '/geojson/bulgaria-regions.geojson' },
  CZ: { geoJsonUrl: '/geojson/czechia-regions.geojson' },
  FI: { geoJsonUrl: '/geojson/finland-regions.geojson' },
  GB: { geoJsonUrl: '/geojson/uk-regions.geojson' },
  HR: { geoJsonUrl: '/geojson/croatia-regions.geojson' },

  NL: { geoJsonUrl: '/geojson/netherlands-regions.geojson' },
  CH: { geoJsonUrl: '/geojson/switzerland-regions.geojson' },
  US: {
    geoJsonUrl: '/geojson/usa-states.geojson',
    regionNameProperty: 'name',
    postcodeProperty: 'ZCTA5CE10',
    postcodeGeoJsonUrl: (regionName) => {
      const file = USA_STATE_FILES[regionName];
      return file ? `/geojson/usa/${file}.geojson` : null;
    },
  },
  RS: { geoJsonUrl: '/geojson/serbia-regions.geojson' },
  NO: { geoJsonUrl: '/geojson/norway-regions.geojson' },
  UA: { geoJsonUrl: '/geojson/ukraine-regions.geojson' },
  TR: { geoJsonUrl: '/geojson/turkey-regions.geojson' },
  AL: { geoJsonUrl: '/geojson/albania-regions.geojson' },
  ME: { geoJsonUrl: '/geojson/montenegro-regions.geojson' },
  MK: { geoJsonUrl: '/geojson/north-macedonia-regions.geojson' },
  XK: { geoJsonUrl: '/geojson/kosovo-regions.geojson' },
  MD: { geoJsonUrl: '/geojson/moldova-regions.geojson' },
  BA: { geoJsonUrl: '/geojson/bosnia-regions.geojson' },
  CA: {
    geoJsonUrl: '/geojson/canada-provinces.geojson',
    regionNameProperty: 'name',
    postcodeProperty: 'CFSAUID',
    postcodeGeoJsonUrl: (regionName: string) => {
      const file = CANADA_PROVINCE_FILES[normalizeCaRegion(regionName)];
      return file ? `/geojson/canada/${file}.geojson` : null;
    },
  },
  MX: { geoJsonUrl: '/geojson/mexico-regions.geojson' },
  IL: { geoJsonUrl: '/geojson/israel-regions.geojson' },
  AD: { geoJsonUrl: '/geojson/andorra-regions.geojson' },
  BR: { geoJsonUrl: '/geojson/brazil-regions.geojson' },
  CN: { geoJsonUrl: '/geojson/china-regions.geojson' },
  CO: { geoJsonUrl: '/geojson/colombia-regions.geojson' },
  AE: { geoJsonUrl: '/geojson/uae-regions.geojson' },
  UY: { geoJsonUrl: '/geojson/uruguay-regions.geojson' },
  UZ: { geoJsonUrl: '/geojson/uzbekistan-regions.geojson' },
  VE: { geoJsonUrl: '/geojson/venezuela-regions.geojson' },
  VN: { geoJsonUrl: '/geojson/vietnam-regions.geojson' },
  PE: { geoJsonUrl: '/geojson/peru-regions.geojson' },
  PR: { geoJsonUrl: '/geojson/puerto-rico-regions.geojson' },
  DZ: { geoJsonUrl: '/geojson/algeria-regions.geojson' },
  AR: { geoJsonUrl: '/geojson/argentina-regions.geojson' },
  AM: { geoJsonUrl: '/geojson/armenia-regions.geojson' },
  AU: {
    geoJsonUrl: '/geojson/australia-states.geojson',
    regionNameProperty: 'STATE_NAME',
    postcodeProperty: 'postcode',
    postcodeGeoJsonUrl: (regionName) => {
      const file = AUSTRALIA_STATE_FILES[regionName];
      return file ? `/geojson/australia/${file}.geojson` : null;
    },
  },
  AZ: { geoJsonUrl: '/geojson/azerbaijan-regions.geojson' },
  BH: { geoJsonUrl: '/geojson/bahrain-regions.geojson' },
  BY: { geoJsonUrl: '/geojson/belarus-regions.geojson' },
  RU: { geoJsonUrl: '/geojson/russia-regions.geojson' },
  BM: { geoJsonUrl: '/geojson/bermuda-regions.geojson' },
  BO: { geoJsonUrl: '/geojson/bolivia-regions.geojson' },
  AF: { geoJsonUrl: '/geojson/afghanistan-regions.geojson' },
  BD: { geoJsonUrl: '/geojson/bangladesh-regions.geojson' },
  MC: { geoJsonUrl: '/geojson/monaco-regions.geojson' },
  MA: { geoJsonUrl: '/geojson/morocco-regions.geojson' },
  CL: { geoJsonUrl: '/geojson/chile-regions.geojson' },
  PY: { geoJsonUrl: '/geojson/paraguay-regions.geojson' },
  EC: { geoJsonUrl: '/geojson/ecuador-regions.geojson' },
  GY: { geoJsonUrl: '/geojson/guyana-regions.geojson' },
  SR: { geoJsonUrl: '/geojson/suriname-regions.geojson' },
  CU: { geoJsonUrl: '/geojson/cuba-regions.geojson' },
  CR: { geoJsonUrl: '/geojson/costa-rica-regions.geojson' },
  PA: { geoJsonUrl: '/geojson/panama-regions.geojson' },
  DO: { geoJsonUrl: '/geojson/dominican-republic-regions.geojson' },
  JM: { geoJsonUrl: '/geojson/jamaica-regions.geojson' },
  HT: { geoJsonUrl: '/geojson/haiti-regions.geojson' },
  JP: { geoJsonUrl: '/geojson/japan-regions.geojson' },
  KR: { geoJsonUrl: '/geojson/south-korea-regions.geojson' },
  TW: { geoJsonUrl: '/geojson/taiwan-regions.geojson' },
  PH: { geoJsonUrl: '/geojson/philippines-regions.geojson' },
  ID: { geoJsonUrl: '/geojson/indonesia-regions.geojson' },
  TH: { geoJsonUrl: '/geojson/thailand-regions.geojson' },
  MY: { geoJsonUrl: '/geojson/malaysia-regions.geojson' },
  KZ: { geoJsonUrl: '/geojson/kazakhstan-regions.geojson' },
  MN: { geoJsonUrl: '/geojson/mongolia-regions.geojson' },
  IN: { geoJsonUrl: '/geojson/india-regions.geojson' },
  GE: { geoJsonUrl: '/geojson/georgia-regions.geojson' },
  TJ: { geoJsonUrl: '/geojson/tajikistan-regions.geojson' },
  KG: { geoJsonUrl: '/geojson/kyrgyzstan-regions.geojson' },
  PG: { geoJsonUrl: '/geojson/papua-new-guinea-regions.geojson' },
  NZ: { geoJsonUrl: '/geojson/new-zealand-regions.geojson' },
  LK: { geoJsonUrl: '/geojson/sri-lanka-regions.geojson' },
  NP: { geoJsonUrl: '/geojson/nepal-regions.geojson' },
  MM: { geoJsonUrl: '/geojson/myanmar-regions.geojson' },
  LA: { geoJsonUrl: '/geojson/laos-regions.geojson' },
  KH: { geoJsonUrl: '/geojson/cambodia-regions.geojson' },
  PK: { geoJsonUrl: '/geojson/pakistan-regions.geojson' },
  IS: { geoJsonUrl: '/geojson/iceland-regions.geojson' },
  GL: { geoJsonUrl: '/geojson/greenland-regions.geojson' },
  MG: { geoJsonUrl: '/geojson/madagascar-regions.geojson' },
  TM: { geoJsonUrl: '/geojson/turkmenistan-regions.geojson' },
  EG: { geoJsonUrl: '/geojson/egypt-regions.geojson' },
  ZA: { geoJsonUrl: '/geojson/south-africa-regions.geojson' },
  GT: { geoJsonUrl: '/geojson/guatemala-regions.geojson' },
  BZ: { geoJsonUrl: '/geojson/belize-regions.geojson' },
  SV: { geoJsonUrl: '/geojson/el-salvador-regions.geojson' },
};

export { countries };
